import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { usePermissions, ROLE_ACCENT } from "@/lib/permissions";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  Users, Radar, FileBarChart2, GraduationCap, Sparkles, Building2,
  Crown, Compass, Wallet, TrendingUp, ArrowRight, ShieldCheck,
} from "lucide-react";
import {
  PageShell, Card, NAVY, CHAMPAGNE, GOLD, IVORY, SLATE,
} from "@/components/altiora/PremiumUI";
import { TopBar } from "@/components/layout/TopBar";

/**
 * AdaptiveHome · Role-aware landing.
 *
 * Renders 4 distinct interfaces without ever building 4 separate URLs:
 *   • Client       → « Ma trajectoire » — personal IMES + priorities
 *   • Advisor      → Studio Clients + Intelligence Commerciale KPIs
 *   • Analyst      → Portfolio-level cross-dossier KPIs
 *   • Institution  → Received bilans + org metrics
 *
 * Design-System-native: uses PageShell + Landing-Grade dark hero header.
 */
export default function AdaptiveHome() {
  const { user } = useAuth();
  const { primaryRole } = usePermissions();
  const persona = mapRoleToPersona(primaryRole);

  return (
    <PageShell testid="adaptive-home-root">
      <TopBar />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 md:px-10 pt-8 md:pt-12 pb-20">
        <PersonaHeader persona={persona} user={user} primaryRole={primaryRole} />
        <div className="mt-8" data-testid="adaptive-home-body">
          {persona === "client"      && <ClientInterface />}
          {persona === "advisor"     && <AdvisorInterface />}
          {persona === "analyst"     && <AnalystInterface />}
          {persona === "institution" && <InstitutionInterface />}
        </div>
      </div>
    </PageShell>
  );
}


// ═════════════════════════════════════════════════════════════════════
// Persona mapping
// ═════════════════════════════════════════════════════════════════════
function mapRoleToPersona(role) {
  if (["founder", "super_admin", "admin"].includes(role)) return "advisor"; // ops/admin default lands on advisor
  if (["professional", "professionnel"].includes(role)) return "advisor";
  if (role === "analyst") return "analyst";
  if (role === "institution") return "institution";
  return "client";
}


function PersonaHeader({ persona, user, primaryRole }) {
  const meta = PERSONA_META[persona];
  const accent = ROLE_ACCENT[primaryRole] || ROLE_ACCENT.member;
  const Icon = meta.icon;
  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, ease: [0.2, 0.7, 0.2, 1] }}
      data-testid={`persona-header-${persona}`}
    >
      <div className="flex items-center gap-3 flex-wrap">
        <span
          className="text-[10px] font-mono uppercase tracking-[0.28em] px-2 py-1 rounded"
          style={{ border: `1px solid ${accent.color}`, color: accent.color }}
          data-testid="persona-role-badge"
        >
          {accent.label}
        </span>
        <div className="text-[10px] font-semibold uppercase tracking-[0.32em]"
                style={{ color: CHAMPAGNE }}>
          ALTIORA · Interface {meta.label_fr}
        </div>
      </div>
      <h1 className="mt-5 font-serif tracking-tight leading-[1.05] font-medium"
              style={{ color: NAVY, fontSize: "clamp(2rem, 4.2vw, 3.4rem)" }}>
        {greet(user)}<br />
        <span style={{ color: GOLD, fontStyle: "italic" }}>{meta.hero_fr}</span>
      </h1>
      <p className="mt-5 max-w-2xl text-[14px] md:text-[15px] leading-relaxed flex items-start gap-2"
             style={{ color: SLATE }}>
        <Icon className="h-4 w-4 mt-0.5 shrink-0" style={{ color: CHAMPAGNE }} strokeWidth={1.8} />
        <span>{meta.sub_fr}</span>
      </p>
    </motion.div>
  );
}

const PERSONA_META = {
  client:      { label_fr: "Client",      icon: Compass,       hero_fr: "voici votre trajectoire d'aujourd'hui.",
                 sub_fr: "Votre diagnostic IMES™, vos priorités du mois et les opportunités que ALTIORA a repérées pour vous — dans un langage humain, sans jargon." },
  advisor:     { label_fr: "Conseiller",  icon: Users,         hero_fr: "vos clients et vos leads — en un coup d'œil.",
                 sub_fr: "Studio Clients, Intelligence Commerciale et les 4 moteurs ALTIORA prêts à générer un bilan exécutif en un clic." },
  analyst:     { label_fr: "Analyste",    icon: FileBarChart2, hero_fr: "votre portefeuille de dossiers, agrégé.",
                 sub_fr: "Vue transversale sur les scores IMES, la bancabilité et les opportunités par cohorte — pour orienter vos analyses stratégiques." },
  institution: { label_fr: "Institution", icon: Building2,     hero_fr: "les dossiers reçus par votre organisation.",
                 sub_fr: "Bilans exécutifs autorisés par les clients, priorisés selon la pertinence pour vos produits — en conformité totale avec le consentement du client." },
};

function greet(user) {
  const first = user?.first_name || (user?.name || "").split(" ")[0] || "Bonjour";
  return `Bonjour ${first},`;
}


// ═════════════════════════════════════════════════════════════════════
// CLIENT
// ═════════════════════════════════════════════════════════════════════
function ClientInterface() {
  const [dossier, setDossier] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/dossiers/");
        const items = (data?.items || data || []);
        setDossier(items[0] || null);
      } catch { /* silent */ }
      finally { setLoading(false); }
    })();
  }, []);

  const imes = dossier?.economic_data?.diagnostic_imes?.last_score;
  const bank = dossier?.economic_data?.bancabilite?.index;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="persona-client-body">
      <CardTile testid="client-tile-imes" icon={TrendingUp} accent="#7A5B14"
        title="Mon score IMES™" hint="Trajectoire économique et sociale"
        value={imes != null ? Number(imes).toFixed(1) : null} suffix="/100"
        to={dossier ? `/app/dossiers/${dossier.dossier_id}` : "/app/dossiers/new"} />
      <CardTile testid="client-tile-bank" icon={Wallet} accent="#065F46"
        title="Ma bancabilité" hint="Préparation aux institutions financières"
        value={bank != null ? Number(bank).toFixed(1) : null} suffix="/100"
        to={dossier ? `/app/dossiers/${dossier.dossier_id}#dossier-bankability-section` : "#"} />
      <CardTile testid="client-tile-opps" icon={Compass} accent="#0B2545"
        title="Opportunités du jour" hint="Programmes, aides et postes cachés"
        to={dossier ? `/app/dossiers/${dossier.dossier_id}/opportunites` : "/app/opportunities"} cta="Voir mes matches" />
      <CardTile testid="client-tile-priorities" icon={Sparkles} accent="#5B21B6"
        title="Mes priorités du mois" hint="3 actions concrètes pour ce mois-ci"
        to="/app/priorities" cta="Ouvrir mes priorités" />
      {loading && (
        <div className="col-span-full text-center text-[13px] italic"
                style={{ color: SLATE }}>
          Chargement de votre trajectoire…
        </div>
      )}
    </div>
  );
}


// ═════════════════════════════════════════════════════════════════════
// ADVISOR
// ═════════════════════════════════════════════════════════════════════
function AdvisorInterface() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="persona-advisor-body">
      <CardTile testid="advisor-tile-studio" icon={Users} accent="#065F46"
        title="Studio Clients" hint="Tous vos dossiers actifs et modules par client"
        to="/app/pro/clients" cta="Ouvrir Studio Clients" />
      <CardTile testid="advisor-tile-commercial" icon={Radar} accent="#7A5B14"
        title="Intelligence Commerciale" hint="Pipeline de leads, cartes de visite, écosystème"
        to="/app/commercial" cta="Ouvrir la console" />
      <CardTile testid="advisor-tile-mentor" icon={GraduationCap} accent="#5B21B6"
        title="Espace Mentor" hint="Votre trajectoire professionnelle personnelle"
        to="/app/mentor" cta="Voir mes objectifs" />
      <CardTile testid="advisor-tile-reports" icon={FileBarChart2} accent="#0B2545"
        title="Rapports générés" hint="Bilans PDF émis pour vos clients"
        to="/app/reports" cta="Historique" />
    </div>
  );
}


// ═════════════════════════════════════════════════════════════════════
// ANALYST — portfolio aggregate
// ═════════════════════════════════════════════════════════════════════
function AnalystInterface() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/dossiers/");
        const items = data?.items || data || [];
        // Aggregate: average IMES, average bank, count by grade
        let sumImes = 0, nImes = 0, sumBank = 0, nBank = 0;
        const gradeDist = { A: 0, B: 0, C: 0, D: 0, E: 0 };
        items.forEach((d) => {
          const imes = d.economic_data?.diagnostic_imes?.last_score;
          const bank = d.economic_data?.bancabilite?.index;
          const g = d.economic_data?.bancabilite?.grade;
          if (imes != null) { sumImes += Number(imes); nImes++; }
          if (bank != null) { sumBank += Number(bank); nBank++; }
          if (g && gradeDist[g] !== undefined) gradeDist[g]++;
        });
        setStats({
          total: items.length,
          avg_imes: nImes ? (sumImes / nImes).toFixed(1) : "—",
          avg_bank: nBank ? (sumBank / nBank).toFixed(1) : "—",
          gradeDist,
        });
      } catch { setStats({ total: 0, avg_imes: "—", avg_bank: "—", gradeDist: {} }); }
    })();
  }, []);

  return (
    <div className="space-y-6" data-testid="persona-analyst-body">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiTile testid="analyst-kpi-total" label="Dossiers suivis"
          value={stats?.total ?? "—"} accent="#0B2545" icon={Users} />
        <KpiTile testid="analyst-kpi-imes" label="IMES™ moyen"
          value={stats?.avg_imes ?? "—"} suffix="/100" accent="#7A5B14" icon={TrendingUp} />
        <KpiTile testid="analyst-kpi-bank" label="Bancabilité moyenne"
          value={stats?.avg_bank ?? "—"} suffix="/100" accent="#065F46" icon={Wallet} />
        <KpiTile testid="analyst-kpi-a-grade" label="Grade A (top)"
          value={stats?.gradeDist?.A ?? 0} accent="#059669" icon={ShieldCheck} />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <CardTile testid="analyst-tile-list" icon={Users} accent="#0B2545"
          title="Explorer tous les dossiers" hint="Vue tabulaire par IMES, bancabilité, secteur"
          to="/app/pro/clients" cta="Ouvrir la liste" />
        <CardTile testid="analyst-tile-reports" icon={FileBarChart2} accent="#7A5B14"
          title="Rapports agrégés" hint="Bilans exécutifs et exports comparés"
          to="/app/reports" cta="Voir l'historique" />
      </div>
    </div>
  );
}


// ═════════════════════════════════════════════════════════════════════
// INSTITUTION — received bilans
// ═════════════════════════════════════════════════════════════════════
function InstitutionInterface() {
  return (
    <div className="space-y-6" data-testid="persona-institution-body">
      <div className="rounded-xl bg-white p-6"
              style={{ border: `1px solid ${CHAMPAGNE}33` }}>
        <div className="text-[10px] font-semibold uppercase tracking-[0.32em]"
                style={{ color: CHAMPAGNE }}>
          En construction · Phase 2
        </div>
        <div className="mt-3 font-serif text-xl md:text-2xl tracking-tight"
                style={{ color: NAVY }}>
          Réception de bilans autorisés par les clients
        </div>
        <p className="mt-3 text-[13px] leading-relaxed max-w-3xl" style={{ color: SLATE }}>
          Cette interface listera les bilans exécutifs partagés par des clients qui vous ont accordé
          un accès explicite. Priorisation automatique par pertinence à vos produits (banque, fonds,
          coopérative), avec traçabilité de consentement et journal d'audit.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <CardTile testid="institution-tile-authorized" icon={ShieldCheck} accent="#5B21B6"
          title="Membres autorisés" hint="Clients qui vous ont donné accès"
          to="/app/partner" cta="Voir la liste" />
        <CardTile testid="institution-tile-directory" icon={Building2} accent="#0B2545"
          title="Annuaire partenaires" hint="Autres institutions du réseau ALTIORA"
          to="/app/partner-directory" cta="Explorer" />
      </div>
    </div>
  );
}


// ═════════════════════════════════════════════════════════════════════
// Shared primitives
// ═════════════════════════════════════════════════════════════════════
function CardTile({ icon: Icon, title, hint, value, suffix, to, cta, accent, testid }) {
  const inner = (
    <motion.div
      whileHover={{ y: -3 }}
      transition={{ duration: 0.2, ease: [0.2, 0.7, 0.2, 1] }}
      className="group h-full rounded-xl bg-white transition-all p-5 flex flex-col gap-3"
      style={{ border: `1px solid ${CHAMPAGNE}33`, boxShadow: "0 2px 8px -3px rgba(11,37,69,0.06)" }}
      data-testid={testid}
    >
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-lg flex items-center justify-center flex-shrink-0"
             style={{ backgroundColor: `${accent}15`, color: accent }}>
          <Icon className="h-5 w-5" strokeWidth={1.7} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-serif text-lg tracking-tight leading-tight" style={{ color: NAVY }}>
            {title}
          </div>
          <div className="mt-0.5 text-[12px]" style={{ color: SLATE }}>{hint}</div>
        </div>
      </div>
      {value != null && (
        <div className="font-serif text-3xl tabular-nums leading-none"
                style={{ color: NAVY }}>
          {value}<span className="text-xs opacity-60 ml-0.5" style={{ color: SLATE }}>{suffix}</span>
        </div>
      )}
      <div className="mt-auto pt-2 inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.24em] transition-transform group-hover:translate-x-0.5"
              style={{ color: CHAMPAGNE }}>
        {cta || "Ouvrir"} <ArrowRight className="h-3 w-3" strokeWidth={2} />
      </div>
    </motion.div>
  );
  return <Link to={to || "#"} className="block h-full">{inner}</Link>;
}

function KpiTile({ label, value, suffix, accent, icon: Icon, testid }) {
  return (
    <div className="rounded-xl bg-white p-4"
             style={{ border: `1px solid ${CHAMPAGNE}22` }}
             data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-semibold uppercase tracking-[0.22em]"
                 style={{ color: SLATE }}>{label}</div>
        <Icon className="h-3.5 w-3.5" style={{ color: accent }} strokeWidth={1.8} />
      </div>
      <div className="mt-2 font-serif text-2xl md:text-3xl tabular-nums leading-none"
              style={{ color: NAVY }}>
        {value}<span className="text-xs opacity-60" style={{ color: SLATE }}>{suffix || ""}</span>
      </div>
    </div>
  );
}
